import { Component, TemplateRef, inject } from '@angular/core';
import { HeaderComponent } from '@components/dashboard/header/header.component';
import { NavComponent } from '@components/dashboard/navigation/nav.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MomentModule } from 'ngx-moment';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Apollo } from 'apollo-angular';
import { AuthStore } from '@store/auth.store';
import { BaseCoreUtils } from '@core/base-core-util';
import { ModuleNames } from '@enums/module-names';
import { USERS_LIST, USER_BOOKING_EXPRESS_TRANS, USER_BOOKING_RIDE_TRANS } from '@gql_queries/users/queries';
import { Booking } from '@interfaces/models.interface';
import { ExcelService } from '@services/excel.service';
import { DashboardService } from '@services/dashboard.service';
import { GoogleMapsModule, GoogleMap } from '@angular/google-maps';

@Component({
  selector: 'app-dashboard-user-details',
  standalone: true,
  imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule, MomentModule, GoogleMapsModule, GoogleMap],
  templateUrl: './dashboard-user-details.component.html',
  styleUrl: './dashboard-user-details.component.css'
})
export class DashboardUserDetailsComponent {
  private offcanvasService = inject(NgbOffcanvas);
  private coreUtils!: BaseCoreUtils;

  constructor(
      private router: Router,
      private route: ActivatedRoute,
      private dasher: DashboardService,
      private excelService: ExcelService,
      private apollo: Apollo,
      private authStore: AuthStore
  ) {
      this.coreUtils = new BaseCoreUtils({
          module: ModuleNames.USERS,
          apollo: this.apollo,
          router: this.router,
          authStore: this.authStore,
          excelService: this.excelService,
      });
  }

  public detailViewed: Record<string, any> = {};
  public bookingdetailViewed: Record<string, any> = {};
  public bookingList: Booking[] = [];
  public booking_currentSelectedPageList: Booking[] = [];

  public consumerTabs2: Record<string, any> = {
    info: true,
    bookings: false,
    rewards: false,
  };

  public bookingcurrentPage: number = 1;
  public disableActionBtnsBooking: any = {
    next: false,
    prev: false,
  };

  selectedServiceType: string = 'express';
  bookingSortBy: string = 'dateCreated';
  bookingSortDirection: string = 'desc';
  public searchKeywordBooking: string = '';

  //FOR THE MAP (BOOKING DETAILS)
  polyLinePath: google.maps.LatLng[] = [];
  display: any;
  center: google.maps.LatLngLiteral = {
      lat: 22.2736308,
      lng: 70.7512555
  };
  zoom = 15;
  polyLineOptions: google.maps.PolylineOptions = {
    strokeColor: '#D81960',
    strokeOpacity: 1.0,
    strokeWeight: 5,
    clickable: false,
    editable: false,
    visible: true,
    geodesic: true,
  };

  ngOnInit(): void {
      this.route.paramMap.subscribe(params => {
          const userId = params.get('id');
          if (userId) {
              this.loadUserDetail(userId);
          }
      });
  }

  loadUserDetail(userId: string) {
      this.coreUtils.viewDetail(USERS_LIST, {
          page: 1,
          searchQuery: userId,
      }, (data) => {
          if (data['users']?.length !== 0) {
              this.detailViewed = data['users'].find((u: any) => u.userId === userId) ?? data['users'][0];
              this.fetchTransactions('express');
          }
      });
  }

  returnToList() {
      this.router.navigate(['/admin/users']);
  }

  detailConsumerTabActive(tabindex: string) {
      Object.keys(this.consumerTabs2).forEach((x) => {
          this.consumerTabs2[x] = false;
      });
      this.consumerTabs2[tabindex] = true;
  }

  fetchTransactions(servicetype: string = 'express', action: string = '') {
      var fromPage = this.bookingcurrentPage;

      if (action != '') {
          switch (action) {
              case 'next':
                  this.bookingcurrentPage++;
                  break;
              case 'prev':
                  if (this.bookingcurrentPage != 1) {
                      this.bookingcurrentPage--;
                  }
                  break;
          }
      }

      let requestQry = null;
      if (servicetype == 'express') {
          requestQry = this.apollo.watchQuery({
              fetchPolicy: 'network-only',
              query: USER_BOOKING_EXPRESS_TRANS,
              variables: {
                  userId: this.detailViewed['userId'],
                  page: this.bookingcurrentPage,
                  sortBy: { sort: this.bookingSortBy, direction: this.bookingSortDirection }
              },
          });
      } else {
          requestQry = this.apollo.watchQuery({
              fetchPolicy: 'network-only',
              query: USER_BOOKING_RIDE_TRANS,
              variables: {
                  page: this.bookingcurrentPage,
                  service: servicetype,
                  userId: this.detailViewed['userId'],
                  sortBy: { sort: this.bookingSortBy, direction: this.bookingSortDirection }
              },
          });
      }

      requestQry.valueChanges.subscribe({
          next: (res: any) => {
              this.bookingList = [];
              this.booking_currentSelectedPageList = [];
              if (servicetype == 'express') {
                  if (res?.data?.bookingExpressTxns?.length) {
                      this.bookingList = res.data.bookingExpressTxns;
                      this.booking_currentSelectedPageList = this.bookingList;
                  } else {
                      this.bookingcurrentPage = fromPage;
                  }
              } else {
                  if (res?.data?.bookingRideTxns?.length) {
                      this.bookingList = res.data.bookingRideTxns;
                      this.booking_currentSelectedPageList = this.bookingList;
                  } else {
                      this.bookingcurrentPage = fromPage;
                  }
              }
          },
          error: (error) => {
              if (error.message === 'Unauthorized' || error?.networkError?.status === 403) {
                  this.authStore.logout().subscribe(() => {
                      this.router.navigate(['/admin/login']);
                  });
              }
          }
      });
  }

  nextPrevBooking(action: string) {
      switch (action) {
          case 'next':
              this.fetchTransactions(this.selectedServiceType, 'next');
              break;
          case 'prev':
              this.fetchTransactions(this.selectedServiceType, 'prev');
              break;
      }
  }

  onServiceTypeChange(event: any) {
      this.fetchTransactions(this.selectedServiceType);
  }

  onBookingSortChange() {
      this.fetchTransactions(this.selectedServiceType);
  }

  searchDataBooking(event: Event) {
      const inputElement = event.target as HTMLInputElement;
      this.searchKeywordBooking = inputElement.value;

      if (this.searchKeywordBooking.trim() === '') {
          this.booking_currentSelectedPageList = this.bookingList;
      } else {
          this.booking_currentSelectedPageList = this.bookingList.filter(object => {
              const searchInObject = (obj: any): boolean => {
                  for (const key in obj) {
                      const value = obj[key];
                      if (value == null) continue;
                      if (typeof value === 'object') {
                          if (searchInObject(value)) return true;
                      } else if (value.toString().toLowerCase().includes(this.searchKeywordBooking.toLowerCase())) {
                          return true;
                      }
                  }
                  return false;
              };
              return searchInObject(object);
          });
      }
  }

  viewBookingDetails(details: Booking, offcanvas: TemplateRef<any>, selectedServiceType: string) {
      this.bookingdetailViewed = details;
      if (selectedServiceType == 'express') {
          if (details.orderRoutes != '') {
              this.polyLinePath = google.maps.geometry.encoding.decodePath(details.orderRoutes);
              const bounds = new google.maps.LatLngBounds();
              this.polyLinePath.forEach((coordinate: google.maps.LatLng) => { bounds.extend(coordinate); });
              this.center = bounds.getCenter().toJSON();
          }
      } else {
          if (details.routes != '') {
              this.polyLinePath = google.maps.geometry.encoding.decodePath(details.routes);
              const bounds = new google.maps.LatLngBounds();
              this.polyLinePath.forEach((coordinate: google.maps.LatLng) => { bounds.extend(coordinate); });
              this.center = bounds.getCenter().toJSON();
          }
      }
      this.offcanvasService.open(offcanvas, { position: 'end' });
  }

  moveMap(event: google.maps.MapMouseEvent) {
      if (event.latLng != null) this.center = event.latLng.toJSON();
  }

  move(event: google.maps.MapMouseEvent) {
      if (event.latLng != null) this.display = event.latLng.toJSON();
  }
}
